import { Box, Grid, Slider, TextField } from '@material-ui/core';
import { formatNumber, RangeType } from '@nemuru-eng/js-api-client';

import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';

import { RANGES_EDITOR_TEXT_VALUES } from './constants/RangesEditorTexts';
import { RangesEditorParams } from './types/rangesEditorParams';
import { RangesEditorTextValues } from './types/rangesEditorTexts';

interface RangesEditorProps<T> {
  params: RangesEditorParams<T>;
  textsByType?: RangesEditorTextValues;
}

export const RangesEditor = ({ params, textsByType }: RangesEditorProps<RangeType>) => {
  const texts = textsByType ?? RANGES_EDITOR_TEXT_VALUES;

  const {
    minAllowedRange,
    maxAllowedRange,
    minGreaterThanMax,
    type,
    maxRange,
    minRange,
    handleInputOnChange,
    handleSliderOnChange,
  } = params;

  const TextFieldStyle = {
    width: '100%',
  };

  return (
    <>
      <Font variant="h3">
        <Text tId={type} />
      </Font>
      <Box mt={3} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            variant="outlined"
            value={minRange}
            label={<Text tId={texts[type].min} />}
            id={`${type}Min`}
            type="number"
            onChange={handleInputOnChange}
            InputProps={params.InputProps}
            style={TextFieldStyle}
            helperText={
              <>
                {minGreaterThanMax && (
                  <Font variant="caption" color="error">
                    <Text tId={texts[type].minGreaterThanMaxError} />
                  </Font>
                )}
                {minRange < minAllowedRange && (
                  <Font variant="caption" color="error">
                    <Text tId={texts[type].minLowerThanMinAllowedError} dynamicDataTexts={[minAllowedRange]} />
                  </Font>
                )}
              </>
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="outlined"
            value={maxRange}
            label={<Text tId={texts[type].max} />}
            id={`${type}Max`}
            type="number"
            onChange={handleInputOnChange}
            InputProps={params.InputProps}
            style={TextFieldStyle}
            inputProps={{ step: 1 }}
            helperText={
              <>
                {maxRange > maxAllowedRange && (
                  <Font variant="caption" color="error">
                    <Text tId={texts[type].maxGreaterThanMaxAllowedError} dynamicDataTexts={[maxAllowedRange]} />
                  </Font>
                )}
              </>
            }
          />
        </Grid>
        <Grid item xs={12} style={{ marginInline: '4px' }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Font variant="caption" color="textSecondary">
              {formatNumber(minAllowedRange, 0)}
            </Font>
            <Font variant="caption" color="textSecondary">
              {formatNumber(maxAllowedRange, 0)}
            </Font>
          </Box>
          <Slider
            color="secondary"
            id={type}
            min={minAllowedRange}
            max={maxAllowedRange}
            value={[minRange, maxRange]}
            onChange={handleSliderOnChange}
          />
        </Grid>
      </Grid>
    </>
  );
};
